import { useEffect, useRef } from 'react';

/**
 * Cloudflare Turnstile (anti-bot) — feat/turnstile.
 *
 * Guard por env: sin `VITE_TURNSTILE_SITE_KEY` (build-time) el componente NO
 * renderiza nada. El backend hace el espejo con `TURNSTILE_SECRET_KEY` (sin
 * ella no exige token).
 *
 * FULLY FAIL-OPEN en el frontend (fix post-#203): el widget es un recolector
 * de token BEST-EFFORT — el caller NUNCA debe gatear el submit con esto. Si
 * el widget no resuelve (sin site key, script bloqueado, dominio no permitido
 * en Cloudflare, outage), el submit va sin token y el BACKEND decide (403
 * genérico solo si el secret está activo). El gating anterior dejaba el
 * login/signup bloqueado ante cualquier fallo del widget.
 *
 * Render EXPLÍCITO (no auto-render): el script se carga una sola vez y cada
 * instancia monta su widget con callback → `onToken(token)`. Token expirado o
 * error → `onToken(null)`.
 */

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;
const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cf-turnstile-script';


declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          appearance?: 'always' | 'execute' | 'interaction-only';
        },
      ) => string;
      remove: (widgetId: string) => void;
    };
  }
}

/** Carga api.js una sola vez; resuelve cuando window.turnstile existe. */
let scriptPromise: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
      const script = existing ?? document.createElement('script');
      if (!existing) {
        script.id = SCRIPT_ID;
        script.src = SCRIPT_SRC;
        script.async = true;
        document.head.appendChild(script);
      }
      script.addEventListener('load', () => resolve());
      script.addEventListener('error', () => {
        scriptPromise = null; // permite reintentar en el próximo mount
        reject(new Error('No se pudo cargar Turnstile'));
      });
    });
  }
  return scriptPromise;
}

export function TurnstileWidget({
  onToken,
  className = '',
  interactionOnly = false,
}: {
  /** §1bis.7 — modo administrado: el widget solo se ve si Cloudflare pide interacción. */
  interactionOnly?: boolean;
  /** Token fresco, o null cuando expira/falla. NO gatear el submit con esto. */
  onToken: (token: string | null) => void;
  className?: string;
}) {
  const container = useRef<HTMLDivElement>(null);
  // Ref para que el widget (montado una vez) siempre llame al onToken vigente.
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (!SITE_KEY) return;
    let widgetId: string | null = null;
    let cancelled = false;
    loadScript()
      .then(() => {
        if (cancelled || !container.current || !window.turnstile) return;
        widgetId = window.turnstile.render(container.current, {
          sitekey: SITE_KEY,
          theme: 'light',
          ...(interactionOnly ? { appearance: 'interaction-only' as const } : {}),
          callback: (token) => onTokenRef.current(token),
          'expired-callback': () => onTokenRef.current(null),
          'error-callback': () => onTokenRef.current(null),
        });
      })
      .catch(() => {
        // Script bloqueado/inalcanzable: no rompemos el form (el backend
        // decidirá con su propia política si acepta el submit sin token).
      });
    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, []);

  if (!SITE_KEY) return null;
  return <div ref={container} className={className} />;
}
